import { Select, SelectVariant } from '@focaldata/cin-ui-components'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import ErrorPage, { ErrorType } from '../../../controls/ErrorPage'
import { Wave } from '../../../data/gql-gen/dashboard/graphql'
import { useProjectId } from '../../../hooks/useProjectId'
import { ResultsPage } from '../MultiMarket/ResultsPage'
import { WaveFlowBreadcrumb } from './WaveFlowBreadcrumb'
import { useWaveId } from './useWaveId'
import { useWaves } from './useWaves'
import { firstFieldingMarketId, hasLaunched } from './waves.utils'

/** Prefer the latest fielding wave, not a newly duplicated, unlaunched draft. */
export const TrackerResultsRedirect = () => {
  const projectId = useProjectId()
  const { waves, loading, error } = useWaves(projectId)
  if (loading) return null
  if (error) return <ErrorPage errorType={ErrorType.GenericError} />
  const wave = waves
    .filter(hasLaunched)
    .sort((a, b) => b.number - a.number)
    .at(0)
  return (
    <Navigate
      to={
        wave
          ? `/project/${projectId}/wave/${wave.waveId}/results`
          : `/project/${projectId}`
      }
      replace
    />
  )
}

/** MMv2 results, with the wave's market set authoritative even if projectById
 * still has only the markets of an earlier wave in its cache. */
export const WaveResultsPage = () => {
  const projectId = useProjectId()
  const waveId = useWaveId()
  const { surveyId } = useParams()
  const navigate = useNavigate()
  const { waves, loading, error } = useWaves(projectId)
  const wave = waves.find((candidate) => candidate.waveId === waveId)

  if (loading) return null
  if (error) return <ErrorPage errorType={ErrorType.GenericError} />
  if (!wave) return <Navigate to={`/project/${projectId}`} replace />

  // Market ids are per wave, so Per Market moves to the other wave's first
  // fielding market rather than dropping back to Combined.
  const resultsPathFor = (target: Wave) => {
    const root = `/project/${projectId}/wave/${target.waveId}/results`
    const market = surveyId && firstFieldingMarketId(target)
    return market ? `${root}/${market}` : root
  }

  return (
    <ResultsPage
      key={waveId}
      wave={wave}
      breadcrumb={<WaveFlowBreadcrumb activeStep="Results" />}
      waveSelector={
        <Select
          variant={SelectVariant.Outlined}
          ariaLabel="Results wave"
          optionValue={waveId}
          options={[...waves]
            .sort((a, b) => a.number - b.number)
            .map((candidate) => ({
              id: candidate.waveId,
              value: candidate.waveId,
              name: candidate.label
            }))}
          onChange={(event) => {
            const target = waves.find(
              (candidate) => candidate.waveId === event.target.value
            )
            if (target) navigate(resultsPathFor(target))
          }}
        />
      }
    />
  )
}
